/**
 * VM Details Panel Component
 * Expandable panel showing detailed VM information
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Typography,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Alert,
  Chip,
  Box,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Computer as ComputerIcon,
  Person as PersonIcon,
  Build as BuildIcon,
  Apps as AppsIcon,
} from '@mui/icons-material';
import { formatDateSafe } from '../utils/dateFormat';

interface InstalledApplication {
  name: string;
  version: string;
}

interface VMDetails {
  vmName: string;
  computerName: string;
  privateIp: string;
  assignedUser: string;
  userEmail: string;
  osVersion: string;
  buildId: string;
  buildDate: string;
  installedApplications: InstalledApplication[];
  status: string;
  runId: string;
  workloadId: string;
}

interface VMDetailsPanelProps {
  vmName: string;
  defaultExpanded?: boolean;
}

export const VMDetailsPanel: React.FC<VMDetailsPanelProps> = ({ vmName, defaultExpanded = false }) => {
  const [details, setDetails] = useState<VMDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(defaultExpanded);

  const fetchVMDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`/api/vms-details/${vmName}`);

      if (!response.ok) {
        throw new Error('Failed to fetch VM details');
      }

      const data = await response.json();
      setDetails(data.data);
    } catch (err) {
      console.error('Error fetching VM details:', err);
      setError(err instanceof Error ? err.message : 'Failed to load VM details');
    } finally {
      setLoading(false);
    }
  }, [vmName]);

  useEffect(() => {
    if (expanded && !details && !loading && !error) {
      fetchVMDetails();
    }
  }, [expanded, details, loading, error, fetchVMDetails]);

  const handleAccordionChange = (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded);
  };

  const formatBuildDate = (dateString: string): string => {
    // formatDateSafe guards parseISO(undefined); keep the prior raw-string
    // fallback for non-empty-but-unparseable build dates (NC-939).
    const formatted = formatDateSafe(dateString, 'dd.MM.yyyy HH:mm');
    return formatted === '—' && dateString ? dateString : formatted;
  };

  return (
    <Accordion expanded={expanded} onChange={handleAccordionChange}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`${vmName}-content`}
        id={`${vmName}-header`}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ComputerIcon color="primary" />
          <Typography variant="subtitle1" fontWeight="medium">
            VM Details: {vmName}
          </Typography>
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        {loading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Loading VM details...
            </Typography>
          </Box>
        )}

        {error && <Alert severity="error">{error}</Alert>}

        {details && !loading && (
          <Grid container spacing={3}>
            {/* VM Configuration */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ComputerIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                <Typography variant="subtitle2" fontWeight="medium">
                  VM Configuration
                </Typography>
              </Box>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Computer Name:
                  </Typography>
                  <Typography variant="body2">{details.computerName}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Private IP:
                  </Typography>
                  <Typography variant="body2">{details.privateIp}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Status:
                  </Typography>
                  <Chip
                    label={details.status}
                    size="small"
                    color={details.status.toLowerCase().includes('running') ? 'success' : 'default'}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Workload:
                  </Typography>
                  <Chip label={details.workloadId} size="small" variant="outlined" />
                </Grid>
              </Grid>
            </Grid>

            {/* Assigned User */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PersonIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                <Typography variant="subtitle2" fontWeight="medium">
                  Assigned User
                </Typography>
              </Box>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Name:
                  </Typography>
                  <Typography variant="body2">{details.assignedUser}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Email:
                  </Typography>
                  <Typography variant="body2">{details.userEmail}</Typography>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Build Information */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <BuildIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                <Typography variant="subtitle2" fontWeight="medium">
                  Build Information
                </Typography>
              </Box>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    OS Version:
                  </Typography>
                  <Typography variant="body2">{details.osVersion}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Build ID:
                  </Typography>
                  <Typography variant="body2" fontFamily="monospace" fontSize="0.85rem">
                    {details.buildId}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Build Date:
                  </Typography>
                  <Typography variant="body2">{formatBuildDate(details.buildDate)}</Typography>
                </Grid>
              </Grid>
            </Grid>

            {/* Installed Applications */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AppsIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                <Typography variant="subtitle2" fontWeight="medium">
                  Installed Applications ({details.installedApplications.length})
                </Typography>
              </Box>
              {details.installedApplications.length === 0 ? (
                <Alert severity="info" sx={{ py: 0.5 }}>
                  No application data available
                </Alert>
              ) : (
                <List dense disablePadding>
                  {details.installedApplications.map((app, index) => (
                    <ListItem key={index} disableGutters>
                      <ListItemText
                        primary={app.name}
                        secondary={`Version ${app.version}`}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Grid>
          </Grid>
        )}
      </AccordionDetails>
    </Accordion>
  );
};
